import type {
  V1NodeExecutedInputs,
  V1NodeExecutedOutputs,
  V1Number,
  Commonv1Value,
  V1HashableValue,
  V1List,
  V1Set,
  V1Dictionary,
  V1TableValue,
  V1FileValue,
  V1TextValue,
  Commonv1ListValue,
  V1SetValue,
  V1DictionaryValue,
} from '@/generated/hooks/bishop/models';
import {
  formatDateDisplay,
  formatTimeDisplay,
  formatDateTimeDisplay,
  formatTimeZoneDisplay,
  formatDurationDisplay,
} from '@/shared/utils';
import {
  decodeBishopFilename,
  getFileDisplayName,
} from '@/shared/utils/file-utils';
import i18n from '@/shared/i18n';
import type { IconType } from '@kognitos/lattice';
import type {
  TResolvedPrimitive,
  TResolvedTable,
  TResolvedFile,
  TResolvedRemoteValue,
  TRemoteValueKind,
  TResolvedValue,
  TResolvedOutputs,
  IMetadataItem,
} from './types';
import { EIDPResultType, type IIDPField } from '@/modules/idp/types';
import {
  normalizeElements,
  normalizeDocumentTable,
} from '@/modules/idp/utils/normalize';
import {
  MAX_RECURSION_DEPTH,
  MAX_PREVIEW_DISPLAY_LENGTH,
  MAX_COMPACT_DISPLAY_LENGTH,
  ZONE_HORIZONTAL_MAX,
  ZONE_VERTICAL_MAX,
  ZONE_LARGE_MEDIA_MAX,
} from './constants';

// Re-export constants for convenience
export { MAX_PREVIEW_DISPLAY_LENGTH, MAX_COMPACT_DISPLAY_LENGTH };

/**
 * Decodes a V1Number to its actual numeric value.
 *
 * The value is calculated as: m / 10^e, where:
 * - m is an unsigned 96-bit integer composed of (lo, mid, hi)
 * - e is the scale (0–28) stored in flags bits 16–23
 * - sign is encoded in flags bit 31 (0 = positive, 1 = negative)
 *
 * @param number - The V1Number object to decode
 * @returns The decoded numeric value as a number
 */
export const decodeNumber = (number: V1Number): number => {
  const lo = number.lo ?? 0;
  const mid = number.mid ?? 0;
  const hi = number.hi ?? 0;
  const flags = number.flags ?? 0;

  // Calculate the mantissa (96-bit integer)
  // Since JavaScript can't handle 96-bit integers directly,
  // we need to be careful with large numbers
  // For most practical cases, the value will fit in JavaScript's number range
  const mantissa = lo + mid * Math.pow(2, 32) + hi * Math.pow(2, 64);

  // Extract the scale from bits 16-23
  const scale = (flags >> 16) & 0xff;

  // Extract the sign from bit 31
  const isNegative = (flags & 0x80000000) !== 0;

  // Calculate the value: m / 10^e
  const value = mantissa / Math.pow(10, scale);

  // Apply the sign
  return isNegative ? -value : value;
};

/**
 * Formats a V1Number to a string with appropriate decimal places.
 *
 * @param number - The V1Number object to format
 * @returns The formatted string representation
 */
export const formatNumber = (number: V1Number): string => {
  const value = decodeNumber(number);

  // Extract the scale to determine decimal places
  const flags = number.flags ?? 0;
  const scale = (flags >> 16) & 0xff;

  // If scale is 0, return as integer
  if (scale === 0) {
    return value.toString();
  }

  // Otherwise, format with the appropriate decimal places
  return value.toFixed(scale);
};

// ============================================================================
// Complex Type Resolvers
// ============================================================================

/**
 * Resolves a list by recursively resolving each item
 */
const resolveList = (list: V1List, depth: number): Array<TResolvedValue> => {
  if (depth > MAX_RECURSION_DEPTH) {
    return [i18n.t('outputs.metadata.maxDepthExceeded', { ns: 'outputs' })];
  }

  if (!list.items || list.items.length === 0) {
    return [];
  }

  return list.items.map(item => resolveCommonValue(item, depth + 1));
};

/**
 * Resolves a set by recursively resolving each hashable item
 */
const resolveSet = (set: V1Set, depth: number): Array<TResolvedValue> => {
  if (depth > MAX_RECURSION_DEPTH) {
    return [i18n.t('outputs.metadata.maxDepthExceeded', { ns: 'outputs' })];
  }

  if (!set.items || set.items.length === 0) {
    return [];
  }

  return set.items.map(item => resolveHashableValue(item, depth + 1));
};

/**
 * Resolves a dictionary by recursively resolving keys and values
 */
const resolveDictionary = (
  dict: V1Dictionary,
  depth: number,
): Record<string, TResolvedValue> => {
  if (depth > MAX_RECURSION_DEPTH) {
    return {
      error: i18n.t('outputs.metadata.maxDepthExceeded', { ns: 'outputs' }),
    };
  }

  if (!dict.entries || dict.entries.length === 0) {
    return {};
  }

  const result: Record<string, TResolvedValue> = {};

  for (const entry of dict.entries) {
    if (entry.key && entry.value) {
      const keyStr = String(resolveHashableValue(entry.key, depth + 1));
      const val = resolveCommonValue(entry.value, depth + 1);
      result[keyStr] = val;
    }
  }

  return result;
};

/**
 * Builds the marker for an offloaded (remote) value. The content isn't fetched
 * here — `RemoteValueRenderer` does that via `paginateValueContent` when
 * mounted. Keeping resolution synchronous lets consumers keep calling
 * `resolveCommonValue` from `useMemo` without plumbing async state through it.
 */
const makeRemoteValue = (
  kind: TRemoteValueKind,
  resource: string,
): TResolvedRemoteValue => ({
  type: 'remote-value',
  kind,
  resource,
});

/**
 * Resolves a text wrapper (`V1TextValue`). Prefers `remote` over `inline` so a
 * backend that populates both by mistake still triggers the offloaded fetch.
 */
const resolveTextValue = (textValue: V1TextValue): TResolvedValue => {
  if (textValue.remote) return makeRemoteValue('text', textValue.remote);
  return textValue.inline ?? '';
};

/**
 * Resolves a list wrapper (`Commonv1ListValue`). Inline content is handed off
 * to the existing `resolveList` recursion.
 */
const resolveListValue = (
  listValue: Commonv1ListValue,
  depth: number,
): TResolvedValue => {
  if (listValue.remote) return makeRemoteValue('list', listValue.remote);
  if (listValue.inline) return resolveList(listValue.inline, depth);
  return [];
};

/**
 * Resolves a set wrapper (`V1SetValue`). Sets are rendered as arrays (matching
 * the legacy `resolveSet` output) so consumers don't need a distinct shape.
 */
const resolveSetValue = (
  setValue: V1SetValue,
  depth: number,
): TResolvedValue => {
  if (setValue.remote) return makeRemoteValue('set', setValue.remote);
  if (setValue.inline) return resolveSet(setValue.inline, depth);
  return [];
};

/**
 * Resolves a dictionary wrapper (`V1DictionaryValue`). Inline content is
 * handed off to the existing `resolveDictionary` recursion.
 */
const resolveDictionaryValue = (
  dictValue: V1DictionaryValue,
  depth: number,
): TResolvedValue => {
  if (dictValue.remote) return makeRemoteValue('dictionary', dictValue.remote);
  if (dictValue.inline) return resolveDictionary(dictValue.inline, depth);
  return {};
};

/**
 * Resolves a table value (remote or inline)
 */
const resolveTable = (table: V1TableValue): TResolvedTable => {
  if (table.remote) {
    return { type: 'table-remote', tableUrl: table.remote };
  }

  if (table.inline) {
    return { type: 'table-inline', data: table.inline.data ?? '' };
  }

  return { type: 'table-inline', data: '' };
};

/**
 * Resolves a file value (remote or inline)
 */
const resolveFile = (file: V1FileValue): TResolvedFile => {
  if (file.remote) {
    return { type: 'file-remote', fileUrl: file.remote };
  }

  if (file.inline) {
    return {
      type: 'file-inline',
      name: file.inline.name ?? '',
      content: file.inline.content ?? '',
    };
  }

  return { type: 'file-inline', name: '', content: '' };
};

// ============================================================================
// Main Resolution Functions
// ============================================================================

/**
 * Resolves a hashable value (used for dictionary keys and set items)
 * Only handles hashable types: bool, number, text, date, timeofday, datetime, duration
 */
const resolveHashableValue = (
  value: V1HashableValue,
  depth: number,
): TResolvedPrimitive => {
  if (depth > MAX_RECURSION_DEPTH) {
    return i18n.t('outputs.metadata.maxDepthExceeded', { ns: 'outputs' });
  }

  if (value.bool_value !== undefined)
    return value.bool_value ? 'true' : 'false';
  if (value.number) return decodeNumber(value.number);
  // Wrapper form (new contract). Only inline is meaningful here — dict keys
  // and set items can't be async-fetched, so a `remote` hashable is treated
  // as an empty string and the backend is expected to never emit that.
  if (value.text_value?.inline !== undefined) return value.text_value.inline;
  if (value.text !== undefined) return value.text;
  if (value.date) return formatDateDisplay(value.date);
  if (value.timeofday) return formatTimeDisplay(value.timeofday);
  if (value.datetime) return formatDateTimeDisplay(value.datetime);
  if (value.duration !== undefined)
    return formatDurationDisplay(value.duration);

  return '';
};

/**
 * Resolves a common value (all 14 types)
 * Main recursive resolution function
 *
 * Handles both old and new API formats:
 * - Old format: { text: "value" } or { number: {...} } etc.
 * - New format: { value: { text: "value" }, type: {...} }
 */
export const resolveCommonValue = (
  value: Commonv1Value,
  depth = 0,
): TResolvedValue => {
  if (depth > MAX_RECURSION_DEPTH) {
    return i18n.t('outputs.metadata.maxDepthExceeded', { ns: 'outputs' });
  }

  // Handle new API format where values are wrapped in { value: {...}, type: {...} }
  // Extract the actual value from the wrapper if it exists
  const actualValue =
    value && typeof value === 'object' && 'value' in value
      ? (value as { value: Commonv1Value }).value
      : value;

  if (!actualValue) return null;
  if (actualValue.null_value !== undefined) return null;
  if (actualValue.bool_value !== undefined)
    return actualValue.bool_value ? 'true' : 'false';
  if (actualValue.number) return decodeNumber(actualValue.number);
  // Wrapper forms take precedence over their legacy direct-field equivalents.
  // Backend is transitioning to wrappers (`text_value`, `list_value`, ...) so
  // that any field can be offloaded; direct fields remain as a compatibility
  // path for payloads the backend still emits in the old shape.
  if (actualValue.text_value) return resolveTextValue(actualValue.text_value);
  if (actualValue.list_value)
    return resolveListValue(actualValue.list_value, depth);
  if (actualValue.set_value)
    return resolveSetValue(actualValue.set_value, depth);
  if (actualValue.dictionary_value)
    return resolveDictionaryValue(actualValue.dictionary_value, depth);
  if (actualValue.text !== undefined) return actualValue.text;
  if (actualValue.date) return formatDateDisplay(actualValue.date);
  if (actualValue.timeofday) return formatTimeDisplay(actualValue.timeofday);
  if (actualValue.datetime) return formatDateTimeDisplay(actualValue.datetime);
  if (actualValue.time_zone)
    return formatTimeZoneDisplay(actualValue.time_zone);
  if (actualValue.duration !== undefined)
    return formatDurationDisplay(actualValue.duration);
  if (actualValue.list) return resolveList(actualValue.list, depth);
  if (actualValue.dictionary)
    return resolveDictionary(actualValue.dictionary, depth);
  if (actualValue.set) return resolveSet(actualValue.set, depth);
  if (actualValue.table) return resolveTable(actualValue.table);
  if (actualValue.file) return resolveFile(actualValue.file);

  return null;
};

/**
 * Extracts and resolves all values from an executed-node payload. Both
 * `V1NodeExecutedInputs` and `V1NodeExecutedOutputs` are structurally
 * `{ [key: string]: Commonv1Value }`, so a single resolver covers both.
 * @param outputData - The raw payload (inputs or outputs) from the backend
 * @returns A dictionary of resolved values
 */
export const extractOutputsValue = (
  outputData: V1NodeExecutedInputs | V1NodeExecutedOutputs,
): TResolvedOutputs => {
  const result: TResolvedOutputs = {};

  for (const [key, value] of Object.entries(outputData)) {
    result[key] = resolveCommonValue(value);
  }

  return result;
};

// ============================================================================
// Type Checking Utilities
// ============================================================================

/**
 * Checks if value is a primitive (null, number, or string)
 *
 * @param value - The resolved value to check
 * @returns True if the value is a primitive type
 */
export const isPrimitive = (
  value: TResolvedValue,
): value is TResolvedPrimitive => {
  return (
    value === null || typeof value === 'number' || typeof value === 'string'
  );
};

// HTML detection signals — looking for either an HTML5 doctype declaration or a
// paired opening/closing tag from a known HTML-element allowlist. Narrow enough
// to skip math expressions ("1 < 2"), generics ("Foo<T>"), and arbitrary
// XML-like fragments. False negatives just fall back to the existing text
// rendering, so it's safe to be conservative.
const HTML_DOCTYPE_REGEX = /<!doctype\s+html\b/i;
const HTML_PAIR_REGEX =
  /<(html|head|body|div|p|span|a|table|tr|td|th|tbody|thead|tfoot|ul|ol|li|h[1-6]|strong|em|b|i|u|blockquote|pre|code|article|section|nav|header|footer|main|aside|figure)\b[^>]*>[\s\S]*?<\/\1\s*>/i;

/**
 * Checks if a value is a string that contains HTML markup. Used to route HTML
 * email bodies and drafted replies to HtmlRenderer instead of rendering them
 * as escaped text in PrimitiveRenderer.
 *
 * @param value - The resolved value to check
 * @returns True when the value is a string containing an HTML5 doctype or a
 *   paired tag from the recognized allowlist
 */
export const isHtmlString = (value: TResolvedValue): value is string => {
  if (typeof value !== 'string') return false;
  return HTML_DOCTYPE_REGEX.test(value) || HTML_PAIR_REGEX.test(value);
};

/**
 * A heading line, a table's header + delimiter rows, or a code fence. A hash
 * read as "number" ("# of items: 3", "## 2 remaining") is not a heading.
 */
const MARKDOWN_BLOCK_REGEX =
  /(^|\n)(#{1,6} (?![Oo]f\b)[^\s\d]|\|.*\|[ \t]*\n\|[ \t]*:?-+:?[ \t]*\||```)/;

/**
 * A heading that starts with a number ("## 1. Summary").
 * One alone reads as plain text; two or more is a numbered document.
 */
const NUMBERED_HEADING_REGEX = /(^|\n)#{1,6} \d/g;

/**
 * Checks if a value is a markdown document: text with at least one block-level
 * markdown structure. Inline emphasis alone doesn't count, so ordinary prose
 * stays plain text.
 *
 * @param value - The resolved value to check
 * @returns True for a non-HTML string with a heading, table or code fence
 */
export const isMarkdownString = (value: TResolvedValue): value is string => {
  if (typeof value !== 'string') return false;
  const numberedHeadings = value.match(NUMBERED_HEADING_REGEX)?.length ?? 0;
  return (
    !isHtmlString(value) &&
    (MARKDOWN_BLOCK_REGEX.test(value) || numberedHeadings >= 2)
  );
};

/**
 * Checks if value is a table object
 *
 * @param value - The resolved value to check
 * @returns True if the value is a table type
 */
export const isTable = (value: TResolvedValue): value is TResolvedTable => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value.type === 'table-remote' || value.type === 'table-inline')
  );
};

/**
 * Checks if value is a file object
 *
 * @param value - The resolved value to check
 * @returns True if the value is a file type
 */
export const isFile = (value: TResolvedValue): value is TResolvedFile => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value.type === 'file-remote' || value.type === 'file-inline')
  );
};

/**
 * Checks if value is an offloaded remote-value marker. The actual content
 * lives in a file referenced by `resource` and is fetched on render.
 */
export const isRemoteValue = (
  value: TResolvedValue,
): value is TResolvedRemoteValue => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'type' in value &&
    value.type === 'remote-value'
  );
};

/**
 * Checks if value is a dictionary (plain object, not array/table/file)
 *
 * @param value - The resolved value to check
 * @returns True if the value is a dictionary type
 */
export const isDictionary = (
  value: TResolvedValue,
): value is { [key: string]: TResolvedValue } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !('type' in value)
  );
};

/**
 * Checks if all values in a dictionary are primitives (null, number, or string).
 * Used to decide between YAML rendering (all primitives) and structured
 * key-value rendering (contains files, tables, arrays, or nested dicts).
 *
 * @param dict - The dictionary to check
 * @returns True if every value in the dictionary is a primitive
 */
export const hasOnlyPrimitiveValues = (dict: {
  [key: string]: TResolvedValue;
}): boolean => {
  return Object.values(dict).every(isPrimitive);
};

/**
 * Extracts the single primary complex value (file or table) from a dictionary
 * where all other values are primitives. Used in the PreviewLayover to unwrap
 * dictionaries like `{ invoice_number: "INV-001", pdf_file: <file> }` so the
 * file renders directly instead of the whole dictionary.
 *
 * @param dict - The dictionary to inspect
 * @returns The file or table value if there is exactly one, null otherwise
 */
export const extractPrimaryComplexValue = (dict: {
  [key: string]: TResolvedValue;
}): TResolvedValue | null => {
  const values = Object.values(dict);
  const complexValues = values.filter(v => !isPrimitive(v));
  if (
    complexValues.length === 1 &&
    (isFile(complexValues[0]) || isTable(complexValues[0]))
  ) {
    return complexValues[0];
  }
  return null;
};

/**
 * Checks if a resolved value supports download.
 * Downloadable types: files and file lists, tables, IDP values, source/code
 * values, and dictionaries. Scalars and browser sessions are not downloadable.
 *
 * @param value - The resolved value to check
 * @returns True if the value can be downloaded
 */
export const isDownloadable = (value: TResolvedValue): boolean => {
  if (isBrowserInstance(value)) return false;
  // Offloaded values are paginated structured data (text/list/dict); we'd need
  // to serialize them into a Blob before handing off to `downloadBlob`, which
  // is out of scope for the initial offloading support.
  if (isRemoteValue(value)) return false;
  const isFileList =
    Array.isArray(value) && value.length > 0 && value.every(isFile);
  return (
    isFile(value) ||
    isFileList ||
    isTable(value) ||
    isDictionary(value) ||
    isIDPOutput(value) ||
    isIDPList(value) ||
    isIDPBundle(value) ||
    isIDPBundleList(value) ||
    isHtmlString(value) ||
    isReadContentOutput(value)
  );
};

/**
 * Set of result_type values that the UI recognises as IDP outputs and
 * routes through the IDP rendering pipeline (document viewer + overlays).
 *
 * NOTE: `classification` is intentionally excluded. Classification results
 * do not carry bounding boxes or document-level fields, so they render as
 * generic dictionaries through the standard output rendering path.
 */
const IDP_RESULT_TYPES: ReadonlySet<string> = new Set<string>([
  EIDPResultType.FIELDS_EXTRACTION,
  EIDPResultType.TABLES_EXTRACTION,
  EIDPResultType.TABLE_RECORDS_EXTRACTION,
  EIDPResultType.LAYOUT_PARSING,
]);

/**
 * Checks whether a value is a read_content result from book-idp.
 *
 * @param value - The resolved value to check
 * @returns True when the value is a read_content result
 */
export function isReadContentOutput(value: TResolvedValue): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return (
    (value as Record<string, TResolvedValue>).result_type ===
    EIDPResultType.READ_CONTENT
  );
}

/**
 * Checks if value is an IDP output using result_type discriminator.
 * Supports both legacy values (extraction_result, document_analysis,
 * table_extraction) and current book-idp values (fields_extraction,
 * tables_extraction, layout_parsing).
 *
 * @param value - The resolved value to check
 * @returns True if the value is an IDP output
 */
export function isIDPOutput(value: TResolvedValue): boolean {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const dict = value as Record<string, TResolvedValue>;

    if ('result_type' in dict) {
      return IDP_RESULT_TYPES.has(dict.result_type as string);
    }
  }

  return false;
}

/**
 * Checks if value is an array where every item is an IDP output.
 * Used to detect list outputs like extracted_tables containing multiple table_extraction results.
 *
 * @param value - The resolved value to check
 * @returns True if the value is a non-empty array of IDP outputs
 */
export function isIDPList(value: TResolvedValue): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(isIDPOutput);
}

/**
 * Checks if value is an "IDP bundle" dict: a plain dictionary that is not
 * itself an IDP output (no top-level `result_type`) but wraps one or more
 * IDP outputs as values. Typical shape:
 *
 * ```
 * {
 *   employee_info: { result_type: "extraction_result", ... },
 *   table: { result_type: "table_extraction", ... },
 *   document: "some.pdf",
 *   metrics: { num_pages: 1 }
 * }
 * ```
 *
 * @param value - The resolved value to check
 * @returns True if the value is a dict wrapping at least one IDP output
 */
export function isIDPBundle(value: TResolvedValue): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  if (isIDPOutput(value)) return false;
  const dict = value as Record<string, TResolvedValue>;
  for (const key of Object.keys(dict)) {
    if (isIDPOutput(dict[key])) return true;
  }
  return false;
}

/**
 * Checks if value is a non-empty array where every item is either an IDP
 * output or an IDP bundle. Superset of `isIDPList`.
 *
 * @param value - The resolved value to check
 * @returns True if the value is a list of IDP outputs or bundles
 */
export function isIDPBundleList(value: TResolvedValue): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(item => isIDPOutput(item) || isIDPBundle(item));
}

/**
 * Collects merged IDP fields from every IDP-typed value within a bundle dict.
 * Values that are not IDP outputs are skipped.
 *
 * @param value - The bundle dict to extract fields from
 * @returns Flattened array of IIDPField
 */
export const getIDPBundleFields = (value: TResolvedValue): IIDPField[] => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return [];
  }
  const dict = value as Record<string, TResolvedValue>;
  const all: IIDPField[] = [];
  for (const key of Object.keys(dict)) {
    const child = dict[key];
    if (isIDPOutput(child)) {
      all.push(...getIDPFieldsFromOutput(child));
    }
  }
  return all;
};

/**
 * Returns IDP fields for an item that is either a standalone IDP output or
 * a bundle dict. Centralises the "IDP item" abstraction used by list tabs.
 *
 * @param value - The item to extract fields from
 * @returns Flattened array of IIDPField
 */
export const getIDPItemFields = (value: TResolvedValue): IIDPField[] => {
  if (isIDPOutput(value)) return getIDPFieldsFromOutput(value);
  if (isIDPBundle(value)) return getIDPBundleFields(value);
  return [];
};

/**
 * Extracts the document name from an IDP bundle dict.
 * Checks the top-level `document`/`source`/`source_document` keys first,
 * then falls back to the first IDP child's document.
 *
 * @param value - The bundle dict
 * @returns The document name string, or undefined if not found
 */
export function getIDPBundleDocumentName(
  value: TResolvedValue,
): string | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const dict = value as Record<string, TResolvedValue>;
  const topLevel = (dict.document || dict.source || dict.source_document) as
    | string
    | undefined;
  if (topLevel) return topLevel;
  for (const key of Object.keys(dict)) {
    const child = dict[key];
    if (isIDPOutput(child)) {
      const childDoc = getIDPDocumentName(child);
      if (childDoc) return childDoc;
    }
  }
  return undefined;
}

/**
 * Derives a tab title for an IDP bundle dict. Prefers a nested `table.title`
 * when present (typical for `table_extraction` payloads), otherwise returns
 * a generic label using the provided index.
 *
 * @param value - The bundle dict
 * @param index - Zero-based index used for the fallback label
 * @returns A human-readable title for the tab
 */
export function getIDPBundleTitle(
  value: TResolvedValue,
  index: number,
): string {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const dict = value as Record<string, TResolvedValue>;
    for (const key of Object.keys(dict)) {
      const child = dict[key];
      if (
        child &&
        typeof child === 'object' &&
        !Array.isArray(child) &&
        isIDPOutput(child)
      ) {
        const childDict = child as Record<string, TResolvedValue>;
        const table = childDict.table as
          | Record<string, TResolvedValue>
          | undefined;
        if (table && typeof table.title === 'string' && table.title) {
          return table.title;
        }
      }
    }
  }
  return i18n.t('outputs.idp.listItemTab', {
    ns: 'outputs',
    index: index + 1,
  });
}

/**
 * Gets the IDP fields from the output.
 * Handles both legacy result types (extraction_result, table_extraction,
 * document_analysis) and current book-idp types (fields_extraction,
 * tables_extraction, layout_parsing). New contract shapes are normalised
 * into the legacy IIDPField union so downstream rendering stays unchanged.
 *
 * @param value - The resolved value to get the IDP fields from
 * @returns The IDP fields normalised into the legacy union
 */
export const getIDPFieldsFromOutput = (value: TResolvedValue): IIDPField[] => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const dict = value as Record<string, TResolvedValue>;
    const resultType = dict.result_type;

    // --- Legacy: extraction_result ---
    if (
      resultType === EIDPResultType.EXTRACTION_RESULT &&
      'fields' in dict &&
      Array.isArray(dict.fields)
    ) {
      return normalizeElements(dict.fields as unknown[]);
    }

    // --- Legacy: table_extraction (singular table) ---
    if (resultType === EIDPResultType.TABLE_EXTRACTION && 'table' in dict) {
      return [dict.table as IIDPField];
    }

    // --- Legacy: document_analysis (flat arrays at top level) ---
    if (resultType === EIDPResultType.DOCUMENT_ANALYSIS) {
      const keys = [
        'tables',
        'key_value_pairs',
        'entities',
        'forms',
        'text_blocks',
        'lists',
      ] as const;
      return keys.flatMap(key =>
        key in dict && Array.isArray(dict[key])
          ? (dict[key] as IIDPField[])
          : [],
      );
    }

    // --- book-idp: fields_extraction (fields array with document_field / text_field elements) ---
    if (
      resultType === EIDPResultType.FIELDS_EXTRACTION &&
      'fields' in dict &&
      Array.isArray(dict.fields)
    ) {
      return normalizeElements(dict.fields as unknown[]);
    }

    // --- book-idp: tables_extraction (tables array with document_idp_table / text_idp_table elements) ---
    if (
      resultType === EIDPResultType.TABLES_EXTRACTION &&
      'tables' in dict &&
      Array.isArray(dict.tables)
    ) {
      return normalizeElements(dict.tables as unknown[]);
    }

    // --- book-idp: table_records_extraction (single merged `records` idp_table) ---
    if (
      resultType === EIDPResultType.TABLE_RECORDS_EXTRACTION &&
      'records' in dict &&
      dict.records != null &&
      typeof dict.records === 'object'
    ) {
      return normalizeElements([dict.records as unknown]);
    }

    // --- book-idp: layout_parsing (nested layout object with tables, key_value_pairs, texts) ---
    if (resultType === EIDPResultType.LAYOUT_PARSING) {
      const layout =
        dict.layout && typeof dict.layout === 'object'
          ? (dict.layout as Record<string, TResolvedValue>)
          : dict;

      const allFields: IIDPField[] = [];

      if ('tables' in layout && Array.isArray(layout.tables)) {
        allFields.push(
          ...(layout.tables as Record<string, unknown>[]).map(
            t => normalizeDocumentTable(t) as IIDPField,
          ),
        );
      }
      if (
        'key_value_pairs' in layout &&
        Array.isArray(layout.key_value_pairs)
      ) {
        allFields.push(
          ...normalizeElements(layout.key_value_pairs as unknown[]),
        );
      }
      if ('texts' in layout && Array.isArray(layout.texts)) {
        allFields.push(...normalizeElements(layout.texts as unknown[]));
      }

      return allFields;
    }
  }

  return [];
};

/**
 * Extracts the document name from an IDP output or IDP list value.
 * Handles both single IDP outputs and IDP lists (arrays of IDP outputs).
 * Checks `document`, `source`, and `source_document` keys to support
 * both legacy and current book-idp contract shapes.
 *
 * @param value - The resolved value to extract the document name from
 * @returns The document name string, or undefined if not found
 */
export function getIDPDocumentName(value: TResolvedValue): string | undefined {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const dict = value as Record<string, TResolvedValue>;
    const topLevel = (dict.document || dict.source || dict.source_document) as
      | string
      | undefined;
    if (topLevel) return topLevel;
    const fields = getIDPFieldsFromOutput(value);
    if (fields[0]?.document) return fields[0].document;
    // Bundle fallback: check IDP children
    if (isIDPBundle(value)) {
      return getIDPBundleDocumentName(value);
    }
    return undefined;
  }
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0] as TResolvedValue;
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const firstDict = first as Record<string, TResolvedValue>;
      const topLevel = (firstDict.document ||
        firstDict.source ||
        firstDict.source_document) as string | undefined;
      if (topLevel) return topLevel;
      const fields = getIDPFieldsFromOutput(first);
      if (fields[0]?.document) return fields[0].document;
      if (isIDPBundle(first)) {
        return getIDPBundleDocumentName(first);
      }
    }
  }
  return undefined;
}

/**
 * Formats a primitive value as a display string
 *
 * @param value - The primitive value to format
 * @returns The formatted string representation
 */
export const formatPrimitive = (value: TResolvedPrimitive): string => {
  if (value === null) return '-';
  return String(value);
};

/**
 * Checks if a primitive value is too long to display inline
 * Long primitives need truncation and an expand button
 *
 * @param value - The primitive value to check
 * @returns True if the value should be truncated, false otherwise
 */
export const isPrimitiveLong = (value: TResolvedPrimitive): boolean => {
  // Null is never long
  if (value === null) {
    return false;
  }

  // Convert to string and check length
  const stringValue = String(value);
  return stringValue.length > MAX_PREVIEW_DISPLAY_LENGTH;
};

/**
 * Determines if a value should use compact horizontal card layout.
 *
 * Compact (horizontal) layout is used for single-line primitive values. The
 * card truncates both the name (fixed max width) and the value (right-aligned
 * ellipsis), so the decision no longer depends on the combined name/value
 * length — only on whether the value is a single, not-too-long line. Multi-line
 * text, HTML bodies, and very long primitives fall back to the taller,
 * expandable vertical card so their content stays readable.
 *
 * @param value - The resolved value to check
 * @returns True if compact horizontal layout should be used
 */
export const shouldUseCompactLayout = (value: TResolvedValue): boolean => {
  if (!isPrimitive(value)) return false;

  // Null values are always short (displayed as '-')
  if (value === null) return true;

  // HTML bodies render in the dedicated, full-height HtmlRenderer card
  if (isHtmlString(value)) return false;

  const valueStr = formatPrimitive(value as TResolvedPrimitive);

  // Multi-line values need the taller vertical card to stay legible
  if (valueStr.includes('\n')) return false;

  // Very long single-line values get the expandable vertical layout rather than
  // being clipped down to a sliver in the compact card.
  return !isPrimitiveLong(value as TResolvedPrimitive);
};

/**
 * Checks if a resolved value is a complex type that needs an expand button
 * Complex types: arrays, dictionaries, tables, files, and long primitives
 * Simple types: null, short numbers, short strings
 *
 * @param value - The resolved value to check
 * @returns True if the value is complex, false otherwise
 */
export const isComplexType = (value: TResolvedValue): boolean => {
  if (
    value === null ||
    typeof value === 'number' ||
    typeof value === 'string'
  ) {
    return isPrimitiveLong(value);
  }
  // Arrays, tables/files, and dictionaries are all complex
  return true;
};

// ============================================================================
// Metadata Extraction Utilities
// ============================================================================

/**
 * Detects the type of a resolved value and returns a label
 * Used for metadata display in the preview layover
 *
 * @param value - The resolved value to detect type for
 * @returns Type label: "text" | "number" | "null" | "table" | "file" | "array" | "dictionary"
 */
export const getValueType = (value: TResolvedValue): string => {
  // Check primitives first
  if (value === null)
    return i18n.t('outputs.metadata.types.null', { ns: 'outputs' });
  if (typeof value === 'number')
    return i18n.t('outputs.metadata.types.number', { ns: 'outputs' });
  if (typeof value === 'string')
    return i18n.t('outputs.metadata.types.text', { ns: 'outputs' });

  // Check arrays
  if (Array.isArray(value))
    return i18n.t('outputs.metadata.types.array', { ns: 'outputs' });

  // Report a remote-value as the type it will become once fetched, so the
  // metadata label in the preview layover reads naturally (e.g. "text", not
  // "remote-value").
  if (isRemoteValue(value)) {
    const kindToTypeKey: Record<TRemoteValueKind, string> = {
      text: 'outputs.metadata.types.text',
      list: 'outputs.metadata.types.array',
      set: 'outputs.metadata.types.array',
      dictionary: 'outputs.metadata.types.dictionary',
    };
    return i18n.t(kindToTypeKey[value.kind], { ns: 'outputs' });
  }

  // Check special object types (table/file). Match on the discriminator itself:
  // a dictionary output can carry its own `type` key holding any value, so
  // treating every object with a `type` key as a table/file is unsound.
  if (isTable(value))
    return i18n.t('outputs.metadata.types.table', { ns: 'outputs' });
  if (isFile(value))
    return i18n.t('outputs.metadata.types.file', { ns: 'outputs' });

  // Everything else is a dictionary
  if (typeof value === 'object' && value !== null)
    return i18n.t('outputs.metadata.types.dictionary', { ns: 'outputs' });

  return i18n.t('outputs.metadata.types.unknown', { ns: 'outputs' });
};

/**
 * Extracts the file name from a file value
 *
 * @param value - The resolved value to extract file name from
 * @returns The file name if the value is a file type, undefined otherwise
 * @remarks
 * - For inline files: returns the name property directly
 * - For remote files: extracts filename from the URL path
 */
const extractFileName = (value: TResolvedValue): string | undefined => {
  if (typeof value === 'object' && value !== null && 'type' in value) {
    const fileValue = value as TResolvedFile;

    // For inline files, use the name property
    if (fileValue.type === 'file-inline') {
      return fileValue.name;
    }

    // For remote files, extract filename from URL
    if (fileValue.type === 'file-remote') {
      const urlParts = fileValue.fileUrl.split('/');
      return urlParts[urlParts.length - 1] || fileValue.fileUrl;
    }
  }

  return undefined;
};

/**
 * Returns a human-readable filename for a resolved file value.
 *
 * @param value - Resolved output/input value
 * @returns Display filename, or undefined when value is not a file
 */
export function getResolvedFileDisplayName(
  value: TResolvedValue,
): string | undefined {
  const rawName = extractFileName(value);
  if (!rawName) return undefined;
  // A canonical Bishop id carries the name base64url-encoded after its `--`.
  return (
    decodeBishopFilename(rawName, { requireCanonicalId: true }) ??
    getFileDisplayName(rawName)
  );
}

// parseFileResourceName moved to @/shared/utils/file-utils — re-exported here for backward compatibility.
export { parseFileResourceName } from '@/shared/utils/file-utils';
export type { IParsedFileResource } from '@/shared/utils/file-utils';

/**
 * Extracts metadata items from a resolved value for preview layover sidebar
 *
 * @param value - The resolved value to extract metadata from
 * @returns Array of metadata items, each containing:
 *   - `label`: Display label (e.g., "[0]", "dictionary", "text")
 *   - `type`: Type of the value (e.g., "null", "number", "text", "array", "dictionary", "table", "file")
 *   - `value`: The actual resolved value
 *   - `fileName`: Optional file name (only present for file types)
 *
 * @remarks
 * - Arrays: Returns array of items with indexed labels [0], [1], etc.
 * - Dictionaries: Returns single item labeled "dictionary"
 * - Primitives/Tables/Files: Returns single item with detected type as label
 */
export const extractMetadataItems = (
  value: TResolvedValue,
  name?: string,
): IMetadataItem[] => {
  const type = getValueType(value);

  // Handle arrays - create an item for each element
  if (Array.isArray(value)) {
    // A list of primitives (e.g. "random numbers") previews as the whole list:
    // return a single item wrapping the full array so the layover renders every
    // element via ArrayRenderer. Splitting it per-element showed only one value
    // behind a sidebar of identically labelled rows, so the full fact value was
    // unreachable (ENG-4042).
    // Records likewise preview as one table, not a sidebar of identically
    // labelled rows.
    if (value.length > 0 && (value.every(isPrimitive) || isRecordList(value))) {
      return [
        {
          label: name ?? type,
          type,
          value,
        },
      ];
    }

    return value.map((item, index) => {
      // For dictionaries with a single file/table, unwrap the primary value
      // so the PreviewLayover renders the file/table directly
      if (isDictionary(item)) {
        const primary = extractPrimaryComplexValue(
          item as Record<string, TResolvedValue>,
        );
        if (primary) {
          const primaryType = getValueType(primary);
          return {
            label: `[${index}]`,
            type: primaryType,
            value: primary,
            fileName: isFile(primary) ? extractFileName(primary) : undefined,
          };
        }
      }

      const itemType = getValueType(item);
      const isFileType = isFile(item);

      return {
        label: `[${index}]`,
        type: itemType,
        value: item,
        fileName: isFileType ? extractFileName(item) : name ? name : undefined,
      };
    });
  }

  // Handle dictionaries - single item labeled "dictionary"
  const isDictionaryValue = isDictionary(value);
  if (isDictionaryValue) {
    return [
      {
        label: i18n.t('outputs.metadata.types.dictionary', { ns: 'outputs' }),
        type: i18n.t('outputs.metadata.types.dictionary', { ns: 'outputs' }),
        value,
        fileName: name ? name : undefined,
      },
    ];
  }

  if (name) {
    return [
      {
        label: name,
        type: type,
        value: value,
      },
    ];
  }

  // Handle primitives, tables, files - single item with detected type
  return [
    {
      label: type,
      type,
      value,
    },
  ];
};

/**
 * Determines the appropriate icon type for a resolved value
 * Used to display visual indicators for different data types in OutputCard
 *
 * @param value - The resolved value to determine icon for
 * @returns Icon type name: "Type" | "Hash" | "List" | "FileSpreadsheet" | "FileType" | "Code"
 *
 * @remarks
 * Icon mapping:
 * - null, string (text/date/time) → "Type"
 * - number → "Hash"
 * - array → "List"
 * - table → "FileSpreadsheet"
 * - file → "FileType"
 * - dictionary (plain object) → "Code"
 */
export const getOutputIconType = (value: TResolvedValue): IconType => {
  // Check primitives first
  if (value === null) return 'Type';
  if (typeof value === 'number') return 'Hash';
  if (typeof value === 'string') {
    // All string types (text, date, time, datetime, timezone, duration) use Type icon
    return 'Type';
  }

  // Check arrays
  if (Array.isArray(value)) return 'List';

  // Remote values use the icon of the kind they'll resolve to, so the card
  // looks the same whether the value was offloaded or sent inline.
  if (isRemoteValue(value)) {
    const kindToIcon: Record<TRemoteValueKind, IconType> = {
      text: 'Type',
      list: 'List',
      set: 'List',
      dictionary: 'Code',
    };
    return kindToIcon[value.kind];
  }

  // Check special object types (table/file). Match on the discriminator itself:
  // a dictionary output can carry its own `type` key holding any value, so
  // treating every object with a `type` key as a table/file is unsound.
  if (isTable(value)) return 'FileSpreadsheet';
  if (isFile(value)) return 'FileType';

  // Browser instances render a globe (matches the Browser Operator replay card)
  if (isBrowserInstance(value)) return 'Globe';

  // Plain objects (dictionaries)
  if (typeof value === 'object' && value !== null) return 'Code';

  // Fallback
  return 'Type';
};

// DEMO: Grid categorization utility - can be removed after demo
/**
 * Categorizes outputs into three groups for grid layout:
 * 1. largeMedia: All files and tables (1-column layout)
 * 2. verticalCards: All other non-horizontal outputs (3-column layout)
 * 3. horizontalCards: Compact horizontal items (3-column layout, shown last)
 *
 * When showAll is false (default), applies zone limits to each category.
 *
 * @param outputs - Array of [key, value] tuples to categorize
 * @param showAll - When true, returns all items; when false, applies zone limits
 * @returns Object with three arrays: largeMedia, verticalCards, horizontalCards
 */
export function categorizeOutputsForGrid(
  outputs: [string, TResolvedValue][],
  showAll: boolean = false,
): {
  largeMedia: [string, TResolvedValue][];
  verticalCards: [string, TResolvedValue][];
  horizontalCards: [string, TResolvedValue][];
} {
  const largeMedia: [string, TResolvedValue][] = [];
  const verticalCards: [string, TResolvedValue][] = [];
  const horizontalCards: [string, TResolvedValue][] = [];

  for (const [key, value] of outputs) {
    // Check if it's a compact horizontal layout item
    const isHorizontal = shouldUseCompactLayout(value);

    if (isHorizontal) {
      horizontalCards.push([key, value]);
      continue;
    }

    // Check if it's a table
    if (isTable(value)) {
      largeMedia.push([key, value]);
      continue;
    }

    // Check if it's a file - ALL files go to large media layout
    if (isFile(value)) {
      largeMedia.push([key, value]);
      continue;
    }

    // Check if it's an IDP output (or a bundle / bundle list / idp list)
    if (
      isReadContentOutput(value) ||
      isIDPOutput(value) ||
      isIDPList(value) ||
      isIDPBundle(value) ||
      isIDPBundleList(value)
    ) {
      largeMedia.push([key, value]);
      continue;
    }

    // Everything else goes to vertical cards
    verticalCards.push([key, value]);
  }

  // Return all items when showing all
  if (showAll) {
    return { largeMedia, verticalCards, horizontalCards };
  }

  // Apply zone limits when collapsed
  return {
    largeMedia: largeMedia.slice(0, ZONE_LARGE_MEDIA_MAX),
    verticalCards: verticalCards.slice(0, ZONE_VERTICAL_MAX),
    horizontalCards: horizontalCards.slice(0, ZONE_HORIZONTAL_MAX),
  };
}

/**
 * Breakpoint configuration for responsive grid spanning.
 * Each key is a Tailwind breakpoint prefix and the value is the column count at that breakpoint.
 */
export type TGridBreakpoints = {
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  '2xl'?: number;
};

/**
 * Calculates the CSS class for grid item spanning in incomplete last rows.
 * When the last row has fewer items than the column count, those items span to fill.
 * Supports responsive grids by accepting breakpoint-specific column counts.
 *
 * @param index - Current item index (0-based)
 * @param totalItems - Total number of items in the grid
 * @param breakpoints - Object mapping breakpoints to column counts (default: { lg: 3 })
 * @returns CSS class string for grid spanning, or empty string if no spanning needed
 *
 * @example
 * // 5 items with lg:grid-cols-3: last row has 2 items
 * getLastRowSpanClass(3, 5, { lg: 3 }) // '' (first item in last row, no span)
 * getLastRowSpanClass(4, 5, { lg: 3 }) // 'lg:col-span-2' (last item spans 2 columns)
 *
 * @example
 * // 4 items with lg:grid-cols-3: last row has 1 item
 * getLastRowSpanClass(3, 4, { lg: 3 }) // 'lg:col-span-3' (last item spans all 3 columns)
 *
 * @example
 * // 5 items with responsive grid xl:grid-cols-2 2xl:grid-cols-3
 * getLastRowSpanClass(4, 5, { xl: 2, '2xl': 3 }) // 'xl:col-span-2 2xl:col-span-2'
 *
 * @example
 * // 4 items with responsive grid xl:grid-cols-2 2xl:grid-cols-3
 * getLastRowSpanClass(3, 4, { xl: 2, '2xl': 3 }) // '2xl:col-span-3'
 */
export function getLastRowSpanClass(
  index: number,
  totalItems: number,
  breakpoints: TGridBreakpoints = { lg: 3 },
): string {
  const classes: string[] = [];

  for (const [breakpoint, columnCount] of Object.entries(breakpoints)) {
    // Skip if no column count or single column (no spanning needed)
    if (!columnCount || columnCount === 1) continue;

    const remainder = totalItems % columnCount;

    // If items divide evenly at this breakpoint, no spanning needed
    if (remainder === 0) continue;

    // Calculate where the last row starts
    const lastRowStartIndex = totalItems - remainder;

    // Only apply spanning to items in the last row
    if (index < lastRowStartIndex) continue;

    // Calculate position in last row
    const positionInLastRow = index - lastRowStartIndex;
    const itemsInLastRow = remainder;

    // Last item in the last row spans remaining columns
    if (positionInLastRow === itemsInLastRow - 1) {
      const columnsToSpan = columnCount - positionInLastRow;
      if (columnsToSpan > 1) {
        classes.push(`${breakpoint}:col-span-${columnsToSpan}`);
      }
    }
  }

  return classes.join(' ');
}

/**
 * Browser instance type with an identifier and a live view URL.
 * V2 uses browser_name (pod name), V4 uses session_id (UUID).
 * The URL appears under 'live_view_url' or 'display_url'.
 * Use {@link getBrowserLiveViewUrl} to read it.
 *
 * Uses a type intersection (not interface) so optional fields don't
 * conflict with the index signature — `undefined` is not TResolvedValue.
 */
export type IBrowserInstanceValue = {
  browser_name?: string;
  session_id?: string;
  task?: string;
} & { [key: string]: TResolvedValue };

/**
 * Returns the live view URL from a browser instance value,
 * checking known key names: live_view_url, display_url.
 */
export function getBrowserLiveViewUrl(value: IBrowserInstanceValue): string {
  return (
    ((value.live_view_url as string | undefined) ??
      (value.display_url as string | undefined)) ||
    ''
  );
}

/**
 * Checks if value is a browser instance dictionary.
 * Requires an identifier (browser_name or session_id) and a URL
 * (live_view_url or display_url).
 */
export const isBrowserInstance = (
  value: TResolvedValue,
): value is IBrowserInstanceValue => {
  if (!isDictionary(value)) {
    return false;
  }

  const dict = value as Record<string, TResolvedValue>;

  const hasIdentifier =
    (typeof dict.browser_name === 'string' && dict.browser_name.length > 0) ||
    (typeof dict.session_id === 'string' && dict.session_id.length > 0);
  if (!hasIdentifier) {
    return false;
  }

  const hasUrl =
    (typeof dict.live_view_url === 'string' && dict.live_view_url.length > 0) ||
    (typeof dict.display_url === 'string' && dict.display_url.length > 0);

  return hasUrl;
};

/**
 * True for a non-empty list of records with no files or tables inside: rows
 * that read best as one table, nested fields included.
 *
 * @param value - The resolved value to inspect
 * @returns True if every item is a plain record
 */
export const isRecordList = (
  value: TResolvedValue,
): value is { [key: string]: TResolvedValue }[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    item =>
      // Not `isDictionary`: it rejects any record with a `type` field.
      typeof item === 'object' &&
      item !== null &&
      !Array.isArray(item) &&
      !containsRenderableMedia(item),
  );

/**
 * Recursively checks whether a value contains renderable media (file, table,
 * browser instance, remote value, read-content result, or IDP output) that
 * needs a dedicated sub-renderer. DictionaryRenderer uses this to choose the
 * structured key-value layout over a flat data code block (MAN-3523).
 *
 * @param value - The resolved value to inspect
 * @returns True if the value or any descendant needs a dedicated renderer
 */
export const containsRenderableMedia = (value: TResolvedValue): boolean => {
  if (isPrimitive(value)) return false;

  if (
    isFile(value) ||
    isTable(value) ||
    isRemoteValue(value) ||
    isBrowserInstance(value) ||
    isReadContentOutput(value) ||
    isIDPOutput(value) ||
    isIDPBundle(value)
  ) {
    return true;
  }

  // IDP lists are arrays of IDP outputs/bundles, so the array recursion below
  // catches them without a dedicated check.
  if (Array.isArray(value)) {
    return value.some(containsRenderableMedia);
  }

  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(containsRenderableMedia);
  }

  return false;
};

/**
 * Whether a resolved value should be rendered as a full inline card rather
 * than a compact chip in dense layouts (run log inputs, etc.). True for
 * value types whose primary content is live and would be useless when
 * collapsed (e.g. a browser handle's screencast). Future live-rendered
 * types extend this predicate; call sites stay generic.
 */
export const shouldRenderInline = (value: TResolvedValue): boolean =>
  isBrowserInstance(value);

/**
 * Matches browser farm URL patterns:
 * - V4: /api/v1/browser/namespaces/{ns}/sessions/{id}/...
 * - V2: /display/{pod-name} (lifecycle-manager internal URLs)
 *
 * /display/ is anchored to avoid false positives from Confluence/Jira-style
 * URLs by requiring it to be followed by a word char (pod names start with
 * alphanumeric, not a path segment like /display/SPACE/page).
 */
const BROWSER_URL_PATTERN = /\/api\/v1\/browser\/|\/display\/\w/;

/** Result from URL-based browser session detection. */
export interface IDetectedBrowserSession {
  key: string;
  identifier: string;
  liveViewUrl: string;
}

/** Known key names that hold browser session identifiers. */
const BROWSER_ID_KEYS = new Set([
  'session_id',
  'browser_name',
  'Session id',
  'Browser name',
]);

const BROWSER_URL_KEYS = [
  'live_view_url',
  'display_url',
  'Live view url',
  'Display url',
];

const BROWSER_PROVISIONING_OUTPUT_KEYS = new Set([
  ...BROWSER_ID_KEYS,
  ...BROWSER_URL_KEYS,
]);

/**
 * Scans resolved outputs for a browser session by detecting URL patterns.
 * Works for any output shape — flat strings, dictionaries, V2 or V4.
 *
 * Identifier resolution (in priority order):
 * 1. Extract from the URL itself (most reliable — matches the actual session)
 * 2. Value of a known ID key (session_id, browser_name, Session id, Browser name)
 * 3. Fallback to 'Browser'
 *
 * Pass 1 checks dictionary values (BDK concept arrived as structured object).
 * Pass 2 checks flat top-level strings (V4 separate "Session id" + "Live view url").
 */
export function detectBrowserSession(
  outputs: Record<string, TResolvedValue>,
): IDetectedBrowserSession | null {
  // Pass 1: dictionary values (e.g. BDK concept arrived as structured object)
  for (const [key, value] of Object.entries(outputs)) {
    if (isDictionary(value)) {
      const dict = value as Record<string, TResolvedValue>;
      let url: string | null = null;
      for (const v of Object.values(dict)) {
        if (typeof v === 'string' && BROWSER_URL_PATTERN.test(v)) {
          url = v;
          break;
        }
      }
      if (url) {
        return {
          key,
          identifier: resolveIdentifier(url, Object.entries(dict)),
          liveViewUrl: url,
        };
      }
    }
  }

  // Pass 2: flat string values (e.g. separate "Session id" + "Live view url")
  let urlKey: string | null = null;
  let urlValue: string | null = null;
  for (const [key, value] of Object.entries(outputs)) {
    if (typeof value === 'string' && BROWSER_URL_PATTERN.test(value)) {
      urlKey = key;
      urlValue = value;
      break;
    }
  }
  if (urlKey && urlValue) {
    return {
      key: urlKey,
      identifier: resolveIdentifier(urlValue, Object.entries(outputs)),
      liveViewUrl: urlValue,
    };
  }

  return null;
}

/**
 * Removes known browser provisioning fields (session IDs, live view URLs)
 * from outputs. Call detectBrowserSession first to also remove the dynamic
 * container key.
 */
export function removeBrowserProvisioningOutputs(
  outputs: Record<string, TResolvedValue>,
): Record<string, TResolvedValue> {
  const filtered = { ...outputs };
  for (const key of BROWSER_PROVISIONING_OUTPUT_KEYS) {
    delete filtered[key];
  }
  return filtered;
}

/** Resolves the session identifier: URL extraction first, then known key names. */
function resolveIdentifier(
  url: string,
  entries: Iterable<[string, TResolvedValue]>,
): string {
  const fromUrl = extractIdFromUrl(url);
  if (fromUrl) return fromUrl;

  for (const [key, value] of entries) {
    if (typeof value === 'string' && BROWSER_ID_KEYS.has(key)) {
      return value;
    }
  }

  return 'Browser';
}

/** Extracts identifier from a browser URL (V4 session id or V2 pod name). */
function extractIdFromUrl(url: string): string | null {
  const v4 = url.match(/\/sessions\/([^/]+)\//);
  if (v4) return v4[1];
  const v2 = url.match(/\/display\/([^/?]+)/);
  if (v2) return v2[1];
  return null;
}

// ============================================================================
// BrowserActionResult Filtering
// ============================================================================

/**
 * Internal BrowserActionResult fields. Quill destructures these into Spy
 * variables under various key shapes — bare (`snapshot`) or prefixed
 * (`nav_snapshot`, `snap_action_type`) — sometimes as a full sibling group,
 * sometimes as a single lone field (e.g. only `page_snapshot`). We drop them
 * all by suffix, accepting that user-defined output variables that happen to
 * share these endings (e.g. `validation_error`, `login_success`,
 * `page_snapshot`) will also be hidden. This is a deliberate trade-off:
 * Quill's lone-field destructuring is indistinguishable from a user variable
 * by shape alone, and the noise-reduction value of dropping BDK internals
 * outweighs the rare collision with user-named outputs. `result` is
 * intentionally absent — it carries the step_intent string we want to
 * surface.
 */
const HIDDEN_BAR_FIELDS = [
  'action_type',
  'success',
  'snapshot',
  'screenshot_uri',
  'error',
];

/** True for a key that exact-matches or ends with `_<field>` for any internal field. */
function isHiddenBarField(key: string): boolean {
  return HIDDEN_BAR_FIELDS.some(f => key === f || key.endsWith(`_${f}`));
}

/** True when value looks like a whole BrowserActionResult concept dict. */
export function isBrowserActionResult(value: TResolvedValue): boolean {
  if (!isDictionary(value)) return false;
  const dict = value as Record<string, TResolvedValue>;
  return (
    typeof dict.action_type === 'string' &&
    'success' in dict &&
    'result' in dict
  );
}

/** Drop internal BrowserActionResult fields; keep only the human-readable result. */
export function filterBrowserActionResultOutputs(
  outputs: Record<string, TResolvedValue>,
): Record<string, TResolvedValue> {
  const filtered: Record<string, TResolvedValue> = {};
  for (const [key, value] of Object.entries(outputs)) {
    if (isBrowserActionResult(value)) {
      const r = (value as Record<string, TResolvedValue>).result;
      if (r !== null && r !== undefined && r !== '') filtered[key] = r;
      continue;
    }
    if (isHiddenBarField(key)) continue;
    filtered[key] = value;
  }
  return filtered;
}

/**
 * Strips browser provisioning fields and BrowserActionResult internals from a
 * set of already-resolved outputs, returning the filtered map plus any
 * detected browser session. Single entry point shared by the run log, Quill
 * chat output renderer, and Run Assistant outputs sheet so they all hide the
 * same plumbing fields the browser widget owns.
 *
 * Filter at the display boundary, not in `extractOutputsValue`. The
 * BrowserPlayer code paths (BrowserRenderer, ChatBrowserSessionWidget) read
 * the raw provisioning fields to render the live screencast.
 */
export function applyDisplayFilters(resolved: Record<string, TResolvedValue>): {
  outputs: Record<string, TResolvedValue>;
  browserSession: IDetectedBrowserSession | null;
} {
  const browserSession = detectBrowserSession(resolved);
  let outputs = resolved;
  if (browserSession) {
    const stripped = removeBrowserProvisioningOutputs(outputs);
    delete stripped[browserSession.key];
    outputs = stripped;
  }
  outputs = filterBrowserActionResultOutputs(outputs);
  return { outputs, browserSession };
}
